import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase/firebaseConfig';

const AuctionCard = ({ auction, onPlaceBid }) => {
  const { t } = useTranslation();
  const { currentUser } = useAuth();
  const isMyAuction = currentUser?.uid === auction.sellerId;
  const [productImage, setProductImage] = useState(null);
  const [imageError, setImageError] = useState(false);

  // Load product image if not available in auction
  useEffect(() => {
    const loadProductImage = async () => {
      // Check if auction already has image
      if (auction.productImage || auction.productImages?.[0] || auction.images?.[0]) {
        setProductImage(auction.productImage || auction.productImages?.[0] || auction.images?.[0]);
        return;
      }

      // Try to load from product if productId exists
      if (auction.productId) {
        try {
          const productRef = doc(db, 'products', auction.productId);
          const productSnap = await getDoc(productRef);
          if (productSnap.exists()) {
            const productData = productSnap.data();
            if (productData.images && productData.images.length > 0) {
              setProductImage(productData.images[0]);
              return;
            }
          }
        } catch (error) {
          console.error('Error loading product image:', error);
        }
      }

      // No image found
      setProductImage(null);
    };

    loadProductImage();
  }, [auction.productId, auction.productImage, auction.productImages, auction.images]);

  // Handle Firestore Timestamp or Date string
  const getEndTime = () => {
    if (!auction.endTime) return null;
    
    // If it's a Firestore Timestamp
    if (auction.endTime.toDate && typeof auction.endTime.toDate === 'function') {
      return auction.endTime.toDate();
    }
    
    // If it's already a Date
    if (auction.endTime instanceof Date) {
      return auction.endTime;
    }
    
    // If it's a string or number, try to parse it
    const parsed = new Date(auction.endTime);
    if (!isNaN(parsed.getTime())) {
      return parsed;
    }
    
    return null;
  };

  const endTime = getEndTime();
  const timeRemaining = endTime 
    ? Math.max(0, Math.floor((endTime.getTime() - new Date().getTime()) / 1000 / 60 / 60))
    : null;

  const formatTimeRemaining = (hours) => {
    if (hours === null || hours === undefined || isNaN(hours)) return t('auction.notSpecified');
    if (hours <= 0) return t('auction.expired');
    if (hours < 24) return `${hours} ชั่วโมง`;
    const days = Math.floor(hours / 24);
    return `${days} วัน`;
  };

  return (
    <Link to={`/marketplace/auctions/${auction.id}`} className="block">
      <div className="bg-white rounded-lg shadow-sm md:shadow-md overflow-hidden hover:shadow-md md:hover:shadow-lg transition-shadow duration-300 border border-yellow-300">
        {/* รูปภาพสินค้า - Shopee Style (Square 1:1) */}
        <div className="relative w-full aspect-square bg-gradient-to-br from-yellow-400 to-yellow-600 overflow-hidden">
          {productImage && !imageError ? (
            <img
              src={productImage}
              alt={auction.productName}
              className="w-full h-full object-cover"
              onError={() => setImageError(true)}
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center relative">
              <img 
                src="/logo.png" 
                alt="CannaTrade Logo" 
                className="w-16 h-16 md:w-24 md:h-24 object-contain opacity-80"
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.parentElement.innerHTML = '<div class="text-white text-2xl md:text-4xl">🔨</div>';
                }}
              />
            </div>
          )}
          {/* Auction Badge */}
          <div className="absolute top-1 left-1 md:top-2 md:left-2 bg-yellow-500 text-white px-1.5 py-0.5 md:px-2 md:py-1 rounded text-xs font-bold">
            🔨 {t('auction.title')}
          </div>
          {/* Time Remaining Badge */}
          {timeRemaining !== null && (
            <div className={`absolute top-1 right-1 md:top-2 md:right-2 px-1.5 py-0.5 md:px-2 md:py-1 rounded text-xs font-semibold ${
              timeRemaining <= 0 
                ? 'bg-red-500 text-white' 
                : timeRemaining < 24 
                  ? 'bg-orange-500 text-white' 
                  : 'bg-green-500 text-white'
            }`}>
              {formatTimeRemaining(timeRemaining)}
            </div>
          )}
        </div>

        {/* Product Info - Shopee Style (Compact) */}
        <div className="p-2 md:p-3">
          {/* ชื่อสินค้า */}
          <h3 className="text-xs md:text-sm font-medium text-gray-800 mb-1.5 md:mb-2 line-clamp-2 min-h-[2.5rem] md:min-h-[3rem] leading-tight">
            {auction.productName}
          </h3>

          {/* ราคาปัจจุบัน - Shopee Style */}
          <div className="mb-1 md:mb-1.5">
            <p className="text-xs text-gray-500 mb-0.5">{t('auction.currentPrice')}</p>
            <p className="text-sm md:text-lg font-bold text-red-500">
              ฿{auction.currentBid?.toLocaleString()}
            </p>
          </div>

          {/* ราคาเริ่มต้นและจำนวนผู้เสนอราคา - Compact */}
          <div className="mb-1.5 md:mb-2 space-y-0.5">
            <p className="text-xs text-gray-500">
              เริ่มต้น: <span className="font-medium text-gray-700">฿{auction.startingPrice?.toLocaleString()}</span>
            </p>
            <p className="text-xs text-gray-500">
              {t('auction.bidders')}: <span className="font-medium text-gray-700">{auction.bids?.length || 0} {t('common.people')}</span>
            </p>
          </div>

          {/* ประเภทประมูล - Small Badge */}
          <div className="mb-1.5 md:mb-2">
            <span className="text-xs bg-blue-100 text-blue-800 px-1.5 py-0.5 rounded">
              {auction.auctionType === 'highest_bid' ? t('auction.bidHighest') : t('auction.title')}
            </span>
          </div>

          {/* ปุ่มเสนอราคา - Shopee Style (Compact) */}
          {!auction.isEnded && auction.status === 'active' && !isMyAuction && (
            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                window.location.href = `/marketplace/auctions/${auction.id}`;
              }}
              className="w-full bg-yellow-500 hover:bg-yellow-600 text-white font-medium py-1.5 md:py-2 px-2 rounded text-xs md:text-sm transition-colors duration-200"
            >
              {t('auction.bid')}
            </button>
          )}
          {!auction.isEnded && auction.status === 'active' && isMyAuction && (
            <div className="w-full bg-gray-100 text-gray-600 font-medium py-1.5 md:py-2 px-2 rounded text-center text-xs md:text-sm">
              {t('auction.myAuction')}
            </div>
          )}

          {auction.isEnded && (
            <div className="w-full bg-gray-300 text-gray-600 font-medium py-1.5 md:py-2 px-2 rounded text-center text-xs md:text-sm">
              {t('auction.expired')}
            </div>
          )}
        </div>
      </div>
    </Link>
  );
};

export default AuctionCard;

